import SAPOMailConfig from '../../../SAPOMailConfig.js';
import AdsSlotObserverControllerSingleton from '@/js/Webmail/Ads/AdsSlotObserverController';

export { GPTAdsControllerSingleton as default };

/**
 * This class is responsible for managing the GPT ads, it is responsible for loading the GPT script,
 * defining the ad slots, refreshing the ad slots and picking the fallback images when there are no
 * ads to display.
 *
 * @author      William Lopes <william.c.lopes@meo.pt>
 * @copyright   Copyright (C) SAPO. All rights reserved.
 * @license     Private
 */
class GPTAdsController
{
    initialized = false;
    
    cmp_max_wait = 1000;

    // prazo (ms) para considerar que o anuncio nao vai renderizar (ex.: gpt.js bloqueado por
    // adblock) e mostrar o fallback na mesma.
    render_check_wait = 2000;

    slot_items = new Map();
    
    slot_queue = new Map();

    get server_ads_config() {
        if (!SAPOMailConfig.server || !SAPOMailConfig.server.ads) {
            return {};
        }
        
        return SAPOMailConfig.server.ads;
    }

    // ---------------------------------------------------------------------------------------------
    // Arranque do GPT e carregamento do script.
    // ---------------------------------------------------------------------------------------------
    
    startGPT() {
        if (this.getInitialized()) {
            return;
        }
        
        this.whenCMPReady(async () =>  {
            const loaded = await this.loadScript();
            
            if (!loaded) {
                return;
            }
            
            this.setInitialized(true);
            this.setupBaseConfig(this.getPPID());
            this.runQueue();
        });
    }
    
    execute(slot_item) {
        this.registerSlotItem(slot_item);
        this.buildSlot(slot_item);
        
        AdsSlotObserverControllerSingleton.observe(
            slot_item,
            () => this.isSlotResized(slot_item),
            () => this.rebuildSlot(slot_item),
        );
    }
    
    addQueue(slot_item) {
        this.defineSize(slot_item);
        this.armRenderCheck(slot_item);

        if (this.initialized === true) {
            this.execute(slot_item);
            return;
        }

        this.slot_queue.set(slot_item.getId(), slot_item);
    }
    
    runQueue() {
        this.slot_queue.forEach((slot_item) => {
            this.execute(slot_item)
        });

        this.slot_queue.clear();
    }

    removeQueue(slot_item) {
        this.slot_queue.delete(slot_item.getId());

        if (!this.slot_items.has(slot_item.getId())) {
            return;
        }

        this.destroySlot(slot_item);
        this.clearRefreshInterval(slot_item);
        this.clearRenderCheck(slot_item);
        AdsSlotObserverControllerSingleton.unobserve(slot_item);
        this.unregisterSlotItem(slot_item);
    }
    
    setupBaseConfig(ppid) {
        window.googletag = window.googletag || { cmd: [] };
        
        window.googletag.cmd.push(() => {
            window.googletag.setConfig({
                disableInitialLoad: true, safeFrame: { forceSafeFrame: true }
            });

            if (ppid) {
                window.googletag.pubads().setPublisherProvidedId(ppid);
            }

            window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                const slot_item = this.slot_items.get(event.slot.getSlotElementId());
                
                if (!slot_item) {
                    return;
                }
                
                slot_item.setIsRendered(true);
                slot_item.setIsCreativeEmpty(event.isEmpty);

                this.clearRenderCheck(slot_item);
                this.updateFallbackVisibility(slot_item);
            });
            
            window.googletag.enableServices();
        });
    }
    
    // - Every 100ms it checks one thing: does window.__tcfapi exist (the consent popup's API)?
    // - As soon as it does, or cmp_max_wait (1s) has passed, it calls callback() and stops.
    // - It doesn't wait for anything the callback does.
    whenCMPReady(callback, deadline = Date.now() + this.cmp_max_wait) {
        if (typeof window.__tcfapi === 'function' || Date.now() >= deadline) {
            callback();
            return;
        }
        
        setTimeout(() => this.whenCMPReady(callback, deadline), 100);
    }
    
    async loadScript() {
        const script = document.createElement('script');
        script.async = true;
        script.src   = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
        
        const loaded = new Promise((resolve) =>{
            script.onload = () => resolve(!!window.googletag);
            script.onerror = () => resolve(false);
        });
        
        document.head.appendChild(script);
        
        return loaded;
    }
    
    // ---------------------------------------------------------------------------------------------
    // Registo e ciclo de vida (orquestradores).
    // ---------------------------------------------------------------------------------------------
    
    registerSlotItem(slot_item) {
        this.slot_items.set(slot_item.getId(), slot_item);
    }

    unregisterSlotItem(slot_item) {
        this.slot_items.delete(slot_item.getId());
    }
    
    // unidade de ciclo de vida: define tamanho/slot, pede o anuncio e arranca o refresh.
    buildSlot(slot_item) {
        this.defineSize(slot_item);
        this.defineSlot(slot_item);
        this.refreshSlot(slot_item);
        this.setRefreshInterval(slot_item);
        this.armRenderCheck(slot_item);
    }

    // resize do slot: destroi e reconstroi no novo tamanho.
    rebuildSlot(slot_item) {
        this.destroySlot(slot_item);
        this.clearRefreshInterval(slot_item);
        this.clearRenderCheck(slot_item);
        this.defineSize(slot_item);
        this.defineSlot(slot_item);
        this.refreshSlot(slot_item);
        this.setRefreshInterval(slot_item);
        this.armRenderCheck(slot_item);
    }
    
    // ---------------------------------------------------------------------------------------------
    // Ciclo de vida: define -> display -> refresh -> destroy.
    // ---------------------------------------------------------------------------------------------
    
    defineSize(slot_item) {
        const sizes = this.getFittingSizes(slot_item.getId(), slot_item.getElement());
        const key   = this.buildSizeKey(sizes);
        
        if (!sizes.length) {
            return;
        }
        
        slot_item.setSizeKey(key);
        slot_item.setSizes(sizes);
    }
    
    defineSlot(slot_item) {
        window.googletag.cmd.push(() => {
            if (!(this.server_ads_config[slot_item.getId()] && this.server_ads_config[slot_item.getId()].sizes)) {
                return;
            }
            
            if (slot_item.getSlot()) {
                return;
            }
            
            if (!slot_item.getSizes()) {
                return;
            }
            
            const gpt_slot = window.googletag.defineSlot(this.server_ads_config[slot_item.getId()].path, slot_item.getSizes(), slot_item.getId());
            
            if (!gpt_slot) {
                return;
            }
            
            gpt_slot.addService(window.googletag.pubads());
            window.googletag.display(gpt_slot);
            
            slot_item.setSlot(gpt_slot);
        });
    }
    
    refreshSlot(slot_item) {
        window.googletag.cmd.push(() => {
            if (!slot_item.getSlot()) {
                return;
            }
            
            if (!this.canRefresh(slot_item)) {
                return;
            }
            
            const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === slot_item.getId());
            
            if (!slot) {
                return;
            }
            
            window.googletag.pubads().refresh([slot], { changeCorrelator: true });
        });
    }
    
    destroySlot(slot_item) {        
        window.googletag.cmd.push(() => {
            const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === slot_item.getId());
            
            if (!slot) {
                return;
            }
            
            window.googletag.destroySlots([slot]);
            
            slot_item.reset();
        });
    }
    
    // ---------------------------------------------------------------------------------------------
    // Refresh: porta de bloqueio e intervalo.
    // ---------------------------------------------------------------------------------------------
    
    canRefresh(slot_item) {
        // tab em background ou browser minimizado
        if (document.visibilityState !== 'visible') {
            return false;
        }
        
        if (!this.isElementVisible(slot_item)) {
            return false;
        }
        
        const rect = slot_item.getElement().getBoundingClientRect();
        
        if (rect.width === 0 || rect.height === 0) {
            return false;
        }
        
        // fora do viewport (ex.: scroll na lista de mensagens)
        const in_viewport = rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
        
        if (!in_viewport) {
            return false;
        }
        
        // tapado por outra view/modal/overlay: o elemento no topo no centro do slot nao e o
        // proprio slot nem um descendente (ex.: o iframe do GPT).
        const top = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
        
        return !!top && slot_item.getElement().contains(top);
    }
    
    setRefreshInterval(slot_item) {
        let refresh_interval = this.server_ads_config[slot_item.getId()].refresh_interval || 30000;

        // Add random oscilatior to interval
        refresh_interval += Math.floor(Math.random() * 5000); // random oscillator up to 5 seconds

        slot_item.setRefreshInterval(setInterval(() => this.refreshSlot(slot_item), refresh_interval));
    }
    
    clearRefreshInterval(slot_item) {
        if (!slot_item.getRefreshInterval()) {
            return;
        }

        clearInterval(slot_item.getRefreshInterval());
        slot_item.setRefreshInterval(null);
    }

    // se o gpt.js for bloqueado (ex.: Brave Shields): nesse caso o
    // slotRenderEnded nunca dispara e a logica do fallback (presa ao googletag.cmd) nunca corre.
    // Este timeout vive FORA do googletag.cmd de proposito; se o slot nao renderizou dentro do
    // prazo e esta visivel, mostra o fallback na mesma.
    armRenderCheck(slot_item) {
        this.clearRenderCheck(slot_item);

        slot_item.setRenderCheck(setTimeout(() => {
            if (slot_item.getIsRendered() || !this.isElementVisible(slot_item)) {
                return;
            }

            slot_item.setFallbackVisibility(true);
            this.setFallbackImage(slot_item);
        }, this.render_check_wait));
    }

    clearRenderCheck(slot_item) {
        if (!slot_item.getRenderCheck()) {
            return;
        }

        clearTimeout(slot_item.getRenderCheck());
        slot_item.setRenderCheck(null);
    }
    
    // ---------------------------------------------------------------------------------------------
    // Helpers de tamanho e visibilidade.
    // ---------------------------------------------------------------------------------------------
    
    // o espaco real onde o anuncio pode ser renderizado e o pai do #id (o #id tem espaco
    // reservado para o botao de fechar e a label PUB), por isso medimos a largura por ai.
    getFittingSizes(id, element) {
        const el      = document.getElementById(id);
        const ad_area = el ? el.parentElement : element;
        return this.server_ads_config[id].sizes.filter((s) => s === 'fluid' || (s[0] <= ad_area.offsetWidth && s[1] <= element.offsetHeight));
    }
    
    buildSizeKey(sizes) {
        return sizes.map(s => s === 'fluid' ? 'fluid' : s.join('x')).join(',');
    }
    
    isSlotResized(slot_item) {
        const sizes = this.getFittingSizes(slot_item.getId(), slot_item.getElement());
        const key   = this.buildSizeKey(sizes);
        
        return slot_item.getSizeKey() !== key;
    }
    
    isElementVisible(slot_item) {
        return slot_item.getElement().getClientRects().length > 0;
    }
    
    // ---------------------------------------------------------------------------------------------
    // Resultado do render: decide entre anuncio real e fallback.
    // ---------------------------------------------------------------------------------------------
    
    updateFallbackVisibility(slot_item) {
        window.googletag.cmd.push(() => {
            if (!slot_item.getSlot()) {
                return;
            }
            
            if (!this.isElementVisible(slot_item)) {
                this.clearRefreshInterval(slot_item);
                slot_item.setFallbackVisibility(false);
                return;
            }
            
            if (!slot_item.getIsRendered()) {
                slot_item.setFallbackVisibility(true);
                this.setFallbackImage(slot_item);
                return;
            }
            
            if (!slot_item.getIsCreativeEmpty()) {
                slot_item.setFallbackVisibility(false);
                return;
            }
            
            slot_item.setFallbackVisibility(true);
            this.setFallbackImage(slot_item);
        });
    }
    
    setFallbackImage(slot_item) {
        const slot_config = this.server_ads_config[slot_item.getId()];
        const fallback    = slot_config && slot_config.fallback;

        if (!fallback) {
            return;
        }

        // entre os tamanhos atuais do slot, escolhe a maior imagem de fallback declarada para ele.
        const candidates = slot_item.getSizes().filter(size => size !== 'fluid' && fallback[size.join('x')]);

        if (!candidates.length) {
            return;
        }

        const best = candidates.reduce((a, b) => (a[0] * a[1] >= b[0] * b[1] ? a : b));

        slot_item.setFallbackImage(fallback[best.join('x')]);
    }
    
    // ---------------------------------------------------------------------------------------------
    // Acessores de estado.
    // ---------------------------------------------------------------------------------------------
    
    setInitialized(value) {
        this.initialized = value;
    }
    
    getInitialized() {
        return this.initialized;
    }
    
    // ---------------------------------------------------------------------------------------------
    // PPID (Publisher Provided ID) para a publicidade.
    // ---------------------------------------------------------------------------------------------

    getPPID() {
        if (!SAPOMailConfig.server || !SAPOMailConfig.server.account) {
            return null;
        }

        return SAPOMailConfig.server.account.ppid || null;
    }
}

const GPTAdsControllerSingleton = new GPTAdsController();
